import { useLazyQuery } from '@apollo/client';
import classNames from 'classnames';
import { isArray, isEqual } from 'lodash';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import Button from '~/components/Button';
import Text from '~/components/Text';
import Colors from '~/constants/colors';
import { QUERY_GET_CONFIGURATIONS_PRODUCT_DETAIL } from '~/data/product/gql';
import ProductVariants from '~/sections/productVariants';
import { Product, ProductVariant } from '~/services/product';
import ModalContainer from '../ModalContainer';
import CustomizationOptions from '~/components/CustomizationOptions';

interface ChangeVariantModalProps {
    open?: boolean;
    product?: Partial<Product>;
    defaultVariant?: Partial<ProductVariant>;
    defaultProductNote?: {
        name: string;
        type: any;
        value: string;
    }[];
    onConfirm?: (
        {
            selectedVariant,
            productNote
        }: {
            selectedVariant: Partial<ProductVariant>;
            productNote: { name: string; type: string; value: string }[];
        },
        parentSKU: string
    ) => void;
    loading?: boolean;
    onCancel?: () => void;
}

const ChangeVariantModal: FC<ChangeVariantModalProps> = ({
    open,
    product,
    defaultVariant,
    defaultProductNote,
    onConfirm,
    onCancel,
    loading
}) => {
    const [getconfig, { data: dataConfig, loading: loadingConfig }] = useLazyQuery(
        QUERY_GET_CONFIGURATIONS_PRODUCT_DETAIL,
        { context: { fetchOptions: { method: 'GET' } } }
    );
    const [validCustomOptions, setValidCustomOptions] = useState({});
    const [valueCustomization, setValueCustomization] = useState<{
        options: string[];
        optionById: { [key: string]: any };
    }>({ options: [], optionById: {} });

    const [selectedVariant, setSelectedVariant] = useState<ProductVariant>(defaultVariant as ProductVariant);

    const variants = useMemo(() => {
        if (isArray(dataConfig?.products?.items?.[0]?.variants)) {
            return dataConfig?.products?.items?.[0]?.variants;
        }
        return product?.variants || [];
    }, [dataConfig?.products?.items?.[0]]);

    const findVariantByAttributes = useCallback(
        (attributes: any) => {
            return variants?.find((variant: any) => {
                return attributes.every((attr: any) => {
                    const variantAttribute = variant.attributes?.find((vAttr: any) => vAttr.code === attr.code);
                    return variantAttribute && variantAttribute.value_index === attr.value_index;
                });
            });
        },
        [variants]
    );

    const onChangeConfig = useCallback(
        (variant: ProductVariant) => {
            if (variant) {
                setSelectedVariant(variant);
            }
        },
        [selectedVariant, findVariantByAttributes, setSelectedVariant]
    );
    const handleOnChangeCustomization = (value: any) => {
        setValueCustomization(value);
    };
    const checkValidateCustomOptions = (data: { options: string[]; optionById: any }) => {
        if (!data) return false;
        if (data.options.length === 0) return false;
        const tempOptionById = data.optionById;
        let valid: any = {};
        const isValid = data?.options
            ?.filter((i: any) => {
                return !!tempOptionById[i]?.required;
            })
            ?.every((item: any) => {
                return !!tempOptionById[item] && !!tempOptionById[item]?.value;
            });
        data?.options?.forEach((item: any) => {
            valid[item] =
                !!tempOptionById[item]?.required && !tempOptionById[item]?.value ? 'This field is required' : '';
            // if (element) {
            //     element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // }
        });

        setValidCustomOptions(valid);

        return !isValid;
    };

    const productNote = useMemo(() => {
        return valueCustomization?.options
            ?.filter((i: any) => {
                const item = valueCustomization?.optionById[i];
                return !!item?.required || !!item?.value;
            })
            ?.map((item) => {
                return {
                    name: valueCustomization.optionById[item].name,
                    type: valueCustomization.optionById[item].type,
                    value: valueCustomization.optionById[item].value + ''
                };
            });
    }, [valueCustomization]);

    const handleConfirm = () => {
        if (!!valueCustomization && checkValidateCustomOptions(valueCustomization)) {
            const element = document.getElementsByName(Object.keys(validCustomOptions)[0])[0];
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        if (selectedVariant && !checkValidateCustomOptions(valueCustomization)) {
            onConfirm?.(
                {
                    selectedVariant,
                    productNote
                },
                product?.sku || ''
            );
        }
    };
    useEffect(() => {
        if (!!product?.configurable_options && product?.configurable_options?.length > 0) return;

        getconfig({
            variables: {
                param: product?.url_key
            }
        });
    }, []);

    const defaultConfig = useMemo(() => {
        if (isArray(dataConfig?.products?.items?.[0]?.configurable_options)) {
            return dataConfig?.products?.items?.[0]?.configurable_options;
        }
        return product?.configurable_options || [];
    }, [dataConfig?.products?.items?.[0]]);

    const defaultProductNoteMemo = useMemo(() => {
        return defaultProductNote?.map((item) => {
            return {
                name: item.name,
                type: item.type,
                value: item.value
            };
        });
    }, [defaultProductNote]);
    return (
        <ModalContainer open={open} title="Edit Item" onClose={onCancel} type="bottom-sheet">
            <div className="flex flex-col gap-6">
                <div className="flex gap-3">
                    <div className="shrink-0">
                        <img
                            className="rounded-lg"
                            src={product?.image?.url || ''}
                            alt={`${product?.name}`}
                            width={68}
                            height={68}
                        />
                    </div>
                    <div className="flex flex-col gap-1 flex-1">
                        <Text type="title-5">{selectedVariant?.product?.name}</Text>
                        <Text type="body-3" color={Colors.GREY_700} className={classNames('capitalize')}>
                            {selectedVariant?.attributes?.map((attr) => attr.label).join(' / ')}
                        </Text>
                        <div className="flex items-center gap-3">
                            <Text type="title-3" className="text-primary">
                                {selectedVariant?.product?.price_range?.minimum_price?.final_price?.symbol || '$'}
                                {selectedVariant?.product?.price_range?.minimum_price?.final_price?.value?.toFixed(2)}
                            </Text>
                            {selectedVariant?.product?.price_range.minimum_price?.discount && (
                                <Text className="line-through" type="body-3" color={Colors.GREY_400}>
                                    {selectedVariant?.product?.price_range?.minimum_price?.regular_price?.symbol || '$'}
                                    {selectedVariant?.product?.price_range?.minimum_price?.regular_price?.value.toFixed(
                                        2
                                    )}
                                </Text>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-3">
                    <ProductVariants
                        defaultConfig={defaultConfig}
                        onChangeConfig={onChangeConfig}
                        product={product as Product}
                        variant={selectedVariant}
                        loading={loadingConfig}
                    />
                </div>
                <CustomizationOptions
                    data={product?.productNote || []}
                    error={validCustomOptions}
                    onChange={handleOnChangeCustomization}
                    value={defaultProductNote}
                    slug={product?.url_key}
                />
            </div>
            <div className="flex gap-5 py-5 sticky bottom-0 bg-white">
                <Button className="flex-1" variant="secondary" type="outline" onClick={onCancel}>
                    Cancel
                </Button>
                <Button
                    disabled={
                        selectedVariant?.product?.sku === defaultVariant?.product?.sku &&
                        isEqual(productNote, defaultProductNoteMemo)
                    }
                    className="flex-1"
                    onClick={() => {
                        handleConfirm();
                    }}
                    loading={loading}
                >
                    Save Changes
                </Button>
            </div>
        </ModalContainer>
    );
};

export default ChangeVariantModal;
